import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, lazy, Suspense } from "react";
import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from "react-router";
import { ApiError } from "./api/client";
import { VoiceButton, VoiceProvider } from "./capture/VoiceAssistant";
import { Icon } from "./components/Icon";
import { useIsPhone } from "./components/useMedia";
import { useSwipe } from "./components/useSwipe";
import { flushShopping } from "./offline/queue";
import { startRealtime } from "./realtime/realtime";
import { CalendarPage } from "./routes/CalendarPage";
import { DisplayPage } from "./routes/DisplayPage";
import { InboxPage } from "./routes/InboxPage";
import { LoginPage } from "./routes/LoginPage";
import { ReviewPage } from "./routes/ReviewPage";
import { SettingsPage } from "./routes/SettingsPage";
import { ShoppingPage } from "./routes/ShoppingPage";
import { TodayPage } from "./routes/TodayPage";
import { SessionProvider, useMe } from "./session";

// Karte (Leaflet) erst bei Bedarf laden – hält den App-Start klein.
const LocationsPage = lazy(() => import("./routes/LocationsPage").then((m) => ({ default: m.LocationsPage })));

export function App() {
  const location = useLocation();
  if (location.pathname.startsWith("/display")) {
    return <DisplayPage />;
  }
  if (location.pathname === "/login") {
    return <LoginPage />;
  }
  return <AuthenticatedApp />;
}

/** Reihenfolge der Bereiche für waagerechtes Wischen außerhalb des Kalenders. */
const SECTIONS = ["/today", "/calendar", "/shopping", "/locations", "/inbox", "/settings"];

function AuthenticatedApp() {
  const me = useMe();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isPhone = useIsPhone();
  const hasSession = !!me.data;

  // Wandtablet kehrt nach einer Stunde ohne Bedienung aus jedem Bereich zum Kalender zurück.
  useEffect(() => {
    if (!hasSession || isPhone) return;
    const idleMs = 60 * 60_000;
    let deadline = Date.now() + idleMs;
    let timer: number;
    const check = () => {
      if (Date.now() >= deadline) {
        localStorage.setItem("fp.calendarMode", "week");
        navigate("/calendar", { replace: true, state: { calendarHome: Date.now() } });
        deadline = Date.now() + idleMs;
      }
      window.clearTimeout(timer);
      timer = window.setTimeout(check, Math.max(0, deadline - Date.now()));
    };
    const activity = () => { deadline = Date.now() + idleMs; check(); };
    const events = ["pointerdown", "keydown", "wheel", "touchstart", "scroll"] as const;
    events.forEach((event) => window.addEventListener(event, activity, { passive: true, capture: true }));
    document.addEventListener("visibilitychange", check);
    window.addEventListener("focus", check);
    check();
    return () => {
      window.clearTimeout(timer);
      events.forEach((event) => window.removeEventListener(event, activity, true));
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("focus", check);
    };
  }, [hasSession, isPhone, navigate]);

  // Der Kalender behandelt Gesten selbst; andere Bereiche folgen weiter der Tableiste.
  const section = SECTIONS.findIndex((p) => location.pathname === p);
  const go = (delta: number) => {
    const next = SECTIONS[section + delta];
    if (section >= 0 && next) navigate(next);
  };
  useSwipe("window", location.pathname === "/calendar" ? {} : { onLeft: () => go(1), onRight: () => go(-1) });

  useEffect(() => {
    if (!me.data) return;
    const stop = startRealtime(qc);
    const flush = () => void flushShopping().then(() => qc.invalidateQueries({ queryKey: ["shopping"] }));
    flush();
    window.addEventListener("online", flush);
    return () => {
      stop();
      window.removeEventListener("online", flush);
    };
  }, [me.data, qc]);

  if (me.isPending) {
    return <div className="center muted">Lade …</div>;
  }
  if (me.error) {
    if (me.error instanceof ApiError && (me.error.status === 401 || me.error.code === "no_family")) {
      return <Navigate to={`/login?returnUrl=${encodeURIComponent(location.pathname)}`} replace />;
    }
    return (
      <div className="center">
        <p className="error">Server nicht erreichbar ({(me.error as ApiError).code}).</p>
        <button className="btn" onClick={() => me.refetch()}>Erneut versuchen</button>
      </div>
    );
  }

  // Tablet: der Kalender nutzt den ganzen Bildschirm und bringt seine eigene Bedienleiste (unten links) mit.
  // Handy: normale Seite mit Tableiste.
  const immersive = location.pathname === "/calendar" && !isPhone;

  return (
    <SessionProvider me={me.data}>
      <VoiceProvider>
      <div className={`shell${immersive ? " immersive" : ""}`}>
        <main className={`content${location.pathname === "/calendar" ? " wide" : ""}`}>
          <Routes>
            <Route path="/" element={<Navigate to="/today" replace />} />
            <Route path="/today" element={<TodayPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/shopping" element={<ShoppingPage />} />
            <Route path="/inbox" element={<InboxPage />} />
            <Route path="/locations" element={<Suspense fallback={<div className="center muted">Karte wird geladen …</div>}><LocationsPage /></Suspense>} />
            <Route path="/review/:planId" element={<ReviewPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="*" element={<Navigate to="/today" replace />} />
          </Routes>
        </main>
        {!immersive && (
          <nav className="tabbar" aria-label="Hauptnavigation">
            <NavLink to="/today"><Icon name="home" /><span>Heute</span></NavLink>
            <NavLink to="/calendar"><Icon name="calendar" /><span>Kalender</span></NavLink>
            <VoiceButton className="tab-voice"><Icon name="mic" /><span>Sprechen</span></VoiceButton>
            <NavLink to="/shopping"><Icon name="shopping" /><span>Einkauf</span></NavLink>
            <NavLink to="/locations"><Icon name="pin" /><span>Orte</span></NavLink>
            <NavLink to="/inbox"><Icon name="inbox" /><span>Eingang</span></NavLink>
            <NavLink to="/settings"><Icon name="settings" /><span>Mehr</span></NavLink>
          </nav>
        )}
        {!immersive && <SwipeHint calendar={location.pathname === "/calendar"} />}
      </div>
      </VoiceProvider>
    </SessionProvider>
  );
}

/** Einmaliger Hinweis auf Touch-Geräten: Wischen wechselt den Bereich (sonst unsichtbar). */
function SwipeHint({ calendar }: { calendar: boolean }) {
  const [show, setShow] = useState(() => {
    try {
      return window.matchMedia?.("(pointer: coarse)").matches === true && localStorage.getItem("fp.swipeHint") !== "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (!show) return;
    try {
      localStorage.setItem("fp.swipeHint", "1");
    } catch {
      // ohne Speicher erscheint der Hinweis eben erneut
    }
    const t = window.setTimeout(() => setShow(false), 6000);
    return () => window.clearTimeout(t);
  }, [show]);
  if (!show) return null;
  return (
    <button type="button" className="swipe-hint" onClick={() => setShow(false)}>
      {calendar ? "← Ansichten → · ↑ später · ↓ früher" : "← Wischen wechselt den Bereich →"}
    </button>
  );
}
